import { useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { isAxiosError } from 'axios';
import {
  Container,
  ContainerInner,
  ThankYou,
  FormContainer,
  Heading,
  Paragraph,
  FormRow,
  FormInput,
  FormButton,
  FormFieldWrapper,
  FormLabel,
  FormSub,
  FormErrorMessage,
  FromSubmitMessage,
  FormFooterText,
  FormContent,
  FormContentInput,
  FormContentLabel,
} from './form.styles';
import { axiosInstance } from '../../config';
import ThankYouPage from '../ThankYouPage';
import CountrySelect from './CountrySelect/CountrySelect';
import { useAppSelector } from '../../store';
import useBackendPath from '../../hooks/useBackendPath';
import useReactEnvs from '../../hooks/useReactEnvs';

const formErrorMessages = {
  general: 'Something went wrong.',
  unexpectedBehaviour:
    'Our systems have detected unusual behaviour from your computer. Please try again later.',
};

const Form = () => {
  const { envs } = useReactEnvs();
  const { selectedAnswers } = useAppSelector((state) => state.answers);
  const { index } = useAppSelector((state) => state.pagination);
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm();

  const [submitPending, setSubmitPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSucces] = useState(false);

  const { backendPath } = useBackendPath();

  const onSubmit: SubmitHandler<FieldValues> = async (formData) => {
    setSubmitPending(true);

    const { email, ...userInfo } = formData;

    try {
      const token = await window.grecaptcha.execute(
        envs?.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'submit' },
      );

      const userData = {
        email,
        questionsAndAnswers: selectedAnswers.map(({ questionId, answers }) => ({
          questionId,
          answers: answers.map(({ id }) => ({ answer: id })),
        })),
        userInfo: {
          ...userInfo,
          country: userInfo.country,
        },
        token,
      };

      const { status } = await axiosInstance.post(
        backendPath + '/api/pdf',
        userData,
        {
          headers: { accept: 'application/json' },
        },
      );

      if (status === 200) {
        setSucces(true);
      } else {
        setError(formErrorMessages.general);
        setSubmitPending(false);
      }
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response &&
        'success' in error.response.data &&
        !error.response.data.success
      ) {
        setError(formErrorMessages.unexpectedBehaviour);
        return;
      }
      setError(formErrorMessages.general);
      setSubmitPending(false);
    }
  };

  if (success) {
    return <ThankYouPage />;
  }

  return (
    <Container show={index > 10}>
      <ContainerInner>
        <ThankYou>
          <Heading>Almost there</Heading>
          <Paragraph>
            Your decarbonization assessment result will be delivered to your
            email address after you complete the form.
          </Paragraph>
        </ThankYou>
        <FormContainer>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormRow>
              <FormFieldWrapper>
                <FormLabel>
                  First name<FormSub>*</FormSub>
                </FormLabel>
                <FormInput
                  type="text"
                  {...register('firstName', { required: true })}
                  isInvalid={errors.firstName !== undefined}
                />
              </FormFieldWrapper>
              {errors.firstName && (
                <FormErrorMessage>This field is required</FormErrorMessage>
              )}
            </FormRow>
            <FormRow>
              <FormFieldWrapper>
                <FormLabel>
                  Last name<FormSub>*</FormSub>
                </FormLabel>
                <FormInput
                  type="text"
                  {...register('lastName', { required: true })}
                  isInvalid={errors.lastName !== undefined}
                />
              </FormFieldWrapper>
              {errors.lastName && (
                <FormErrorMessage>This field is required</FormErrorMessage>
              )}
            </FormRow>
            <FormRow>
              <FormFieldWrapper>
                <FormLabel>
                  Company name<FormSub>*</FormSub>
                </FormLabel>
                <FormInput
                  type="text"
                  {...register('companyName', { required: true })}
                  isInvalid={errors.companyName !== undefined}
                />
              </FormFieldWrapper>
              {errors.companyName && (
                <FormErrorMessage>This field is required</FormErrorMessage>
              )}
            </FormRow>
            <FormRow>
              <FormFieldWrapper>
                <FormLabel>
                  Job title<FormSub>*</FormSub>
                </FormLabel>
                <FormInput
                  type="text"
                  {...register('jobTitle', { required: true })}
                  isInvalid={errors.jobTitle !== undefined}
                />
              </FormFieldWrapper>
              {errors.jobTitle && (
                <FormErrorMessage>This field is required</FormErrorMessage>
              )}
            </FormRow>
            <FormRow>
              <FormFieldWrapper>
                <FormLabel>
                  Email address<FormSub>*</FormSub>
                </FormLabel>
                <FormInput
                  id="email"
                  type="text"
                  autoComplete="email"
                  {...register('email', {
                    required: {
                      value: true,
                      message: 'This field is required',
                    },
                    pattern: {
                      value:
                        // eslint-disable-next-line no-useless-escape
                        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: 'Please use valid email address',
                    },
                  })}
                  isInvalid={errors.email !== undefined}
                />
              </FormFieldWrapper>
              {errors.email && (
                <FormErrorMessage>
                  <>{errors.email.message}</>
                </FormErrorMessage>
              )}
            </FormRow>
            <FormRow>
              <FormFieldWrapper>
                <FormLabel>Country</FormLabel>
                <CountrySelect control={control} />
              </FormFieldWrapper>
            </FormRow>
            <FormFooterText>
              By providing my email address, I understand that DHL Global
              Forwarding will send me a personalized DGF Logistics
              Decarbonization Assessment result, and I may be contacted by a DHL
              Global Forwarding representative by email to discuss my results.
              DHL Global Forwarding will not share your data with any third
              parties. Read our full Privacy statement{' '}
              <a
                href="https://www.dhl.com/global-en/home/footer/privacy-notice.html"
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
              .
            </FormFooterText>
            <FormContent>
              <FormContentInput
                id="marketingConsent"
                type="checkbox"
                {...register('marketingConsent')}
              />
              <FormContentLabel htmlFor="marketingConsent">
                I agree to receive marketing material by email about offers,
                news, products and services from DHL Global Forwarding GmbH on a
                regular basis. I can withdraw my consent at any time by clicking
                the unsubscribe button at the end of each email.
              </FormContentLabel>
            </FormContent>
            <FormButton
              type="submit"
              value={submitPending ? 'Submitting..' : 'Send me my report'}
              disabled={submitPending || error !== null}
            />
            {error && <FromSubmitMessage>{error}</FromSubmitMessage>}
          </form>
        </FormContainer>
      </ContainerInner>
    </Container>
  );
};

export default Form;
